import API from "../../Utils/api";
import React, { useState, useEffect } from "react";
import { MdOutlineDateRange } from "react-icons/md";
import { TfiPencil } from "react-icons/tfi";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Bannerimg from "../../Assets/banner.png";
import Banner from "../../components/UserSideComponents/common/Banner";
import toast from "react-hot-toast";

const GalleryCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
    <div className="w-full h-52 bg-gray-200" />
    <div className="p-5 space-y-3">
      <div className="h-3 bg-gray-200 rounded w-1/3" />
      <div className="h-5 bg-gray-200 rounded w-full" />
      <div className="h-5 bg-gray-200 rounded w-4/5" />
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-3 bg-gray-200 rounded w-3/4" />
      <div className="h-8 bg-gray-200 rounded-lg w-28 mt-2" />
    </div>
  </div>
);

const ITEMS_PER_PAGE = 9;

const Gallery = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    setLoading(true);
    try {
      const res = await API.get(
        `/gallery/get-all?page=${currentPage}&limit=${ITEMS_PER_PAGE}&search=${search}`,
      );

      setGallery(res.data?.gallery || []);
      setTotalPages(res.data?.totalPages || 1);
      setTotal(res.data?.total || 0);
    } catch (error) {
      console.error("Error fetching gallery:", error);
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchGallery();
    }, 500);

    return () => clearTimeout(delay);
  }, [search, currentPage]);

  const featuredGallery = gallery[0];
  const restGallery = gallery.slice(1);

  return (
    <>
      <Banner heading="Our Gallery" image={Bannerimg} />

      <div className="w-full px-4 sm:px-6 md:px-10 py-10 bg-yellow-50/30 min-h-screen">
        {/* Header */}
        <div className="flex flex-col items-center justify-center text-center mb-10">
          <p className="text-[#92AD36] text-sm font-semibold tracking-widest uppercase">
            Our Company Gallery
          </p>
          <h2 className="text-2xl md:text-4xl font-serif  text-black mt-3">
            Latest Gallery
          </h2>
          <div className="flex mt-4 gap-2">
            <span className="w-10 h-1 bg-[#92AD36]" />
            <span className="w-3 h-1 bg-[#92AD36]" />
            <span className="w-5 h-1 bg-[#92AD36]" />
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex justify-center mb-10">
          <div className="relative w-full max-w-lg">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search gallery by heading"
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#92AD36] shadow-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-400 transition text-xs"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && !loading && (
          <div className="flex flex-col items-center py-20 text-center">
            <span className="text-5xl mb-4">😕</span>
            <p className="text-gray-500">{error}</p>
            <button
              onClick={fetchGallery}
              className="mt-4 px-6 py-2 bg-[#92AD36] text-white rounded-xl text-sm hover:bg-[#7a9230] transition"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && total === 0 && (
          <div className="flex flex-col items-center py-20 text-center">
            <span className="text-5xl mb-4">📝</span>
            <p className="text-gray-600 font-medium text-lg">
              No Gallery found
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {search
                ? "Try a different search term"
                : "No gallery published yet"}
            </p>
            {search && (
              <button
                onClick={() => setSearch("")}
                className="mt-3 px-5 py-1.5 bg-[#92AD36] text-white rounded-lg text-xs"
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {/* ── Featured Blog (first blog, full width) ── */}
        {!loading &&
          !error &&
          featuredGallery &&
          !search &&
          currentPage === 1 && (
            <div
              // onClick={() => navigate(`/blog/${featuredBlog._id}`)}
              className="relative w-full rounded-2xl overflow-hidden shadow-md mb-10 cursor-pointer group"
            >
              <img
                src={featuredGallery.image?.[0]}
                alt={featuredGallery.heading}
                className="w-full h-64 sm:h-80 md:h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => {
                  e.target.src = "https://placehold.co/1200x400?text=Blog";
                }}
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-white text-xl sm:text-2xl md:text-3xl font-bold font-serif leading-snug line-clamp-2">
                  {featuredGallery.heading}
                </h3>
              </div>
            </div>
          )}

        {/* ── Blog Grid ── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols- gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <GalleryCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
            {(search ? gallery : restGallery).map((item) => (
              <div
                key={item._id}
                // onClick={() => navigate(`/blog/${item._id}`)}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:border-[#92AD36] hover:shadow-xl transition-all duration-300 group cursor-pointer"
              >
                {/* Image */}
                <div className="relative overflow-hidden h-52">
                  <img
                    src={item.image?.[0]}
                    alt={item.heading}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = "https://placehold.co/600x300?text=Blog";
                    }}
                  />
                  {/* Corner Badge */}
                </div>

                {/* Content */}
                {/* <div className="p-5">
                  
                  <h3 className="text-base font-bold text-black line-clamp-2 leading-snug group-hover:text-[#3A7138] transition-colors duration-200">
                    {item.heading}
                  </h3>
                </div> */}
              </div>
            ))}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              ← Prev
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setCurrentPage(i + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className={`w-9 h-9 rounded-lg text-sm font-medium transition ${
                  currentPage === i + 1
                    ? "bg-[#92AD36] text-white"
                    : "border border-gray-300 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => {
                setCurrentPage((p) => Math.min(p + 1, totalPages));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Next →
            </button>
          </div>
        )}

        {/* Results count */}
        {!loading && total > 0 && (
          <p className="text-center text-xs text-gray-400 mt-4">
            Showing{" "}
            <span className="font-semibold text-gray-600">
              {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
              {Math.min(currentPage * ITEMS_PER_PAGE, total)}
            </span>{" "}
            of <span className="font-semibold text-gray-600">{total}</span>{" "}
            gallery
          </p>
        )}
      </div>
    </>
  );
};
export default Gallery;
